import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInDays, endOfMonth, format, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { ArrowRight, Check, ExternalLink, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import {
  HMRC_DEADLINE_SUPPORT,
  HMRC_DEADLINE_TYPE_OPTIONS,
  getHmrcDeadlineAction,
  getHmrcReminderWindowLabel,
  type HmrcDeadlineType,
} from '@/lib/work-deadlines';
import InvoiceTracker from './InvoiceTracker';
import {
  buildWorkBusinessBankProjection,
  buildWorkCashflowSnapshot,
  type SharedWorkInvoiceEntry,
  type WorkBusinessBankAccountInput,
  type WorkBusinessBankTransactionInput,
} from '../../../ui-foundation/features/work';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppErrorState,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppChipButton, AppIconButton, AppNativeSelect, AppPanelButton } from '../../../ui-foundation/adapters/web';

type ProjectKey = 'adulting' | 'kindred' | 'adhd' | 'other';
type ProjectFilter = 'all' | ProjectKey;
type SourceType = 'employment' | 'self_employed' | 'other';
type RecurringPeriod = 'monthly' | 'yearly' | 'none';

interface Deadline {
  id: string;
  title: string;
  deadline_date: string;
  type: string;
  notes: string | null;
  completed: boolean;
}

interface IncomeEntry {
  id: string;
  amount: number;
  source: string | null;
  source_type: SourceType | null;
  project: ProjectKey | null;
  notes: string | null;
  received_date: string;
}

interface ExpenseEntry {
  id: string;
  amount: number;
  category: string;
  date: string;
  description: string;
  expense_scope: 'personal' | 'business' | null;
  project: ProjectKey | null;
  is_recurring: boolean;
  recurring_period: RecurringPeriod | null;
  notes: string | null;
}

interface WorkInvoiceEntry extends SharedWorkInvoiceEntry {}

interface ContentCard {
  id: string;
  category: string;
  title: string;
  summary: string;
  body: string;
  icon: string;
  link_label: string | null;
  link_url: string | null;
  last_reviewed_at: string;
  sort_order: number;
}

const DEFAULT_DEADLINES: Array<{ title: string; deadline_date: string; type: HmrcDeadlineType }> = [
  { title: 'Self Assessment tax return (2024/25)', deadline_date: '2026-01-31', type: 'self-assessment' },
  { title: 'Payment on account (first payment)', deadline_date: '2026-01-31', type: 'payment-on-account' },
  { title: 'Payment on account (second payment)', deadline_date: '2026-07-31', type: 'payment-on-account' },
  { title: 'End of 2025/26 tax year', deadline_date: '2026-04-05', type: 'other' },
  { title: 'New tax year begins', deadline_date: '2026-04-06', type: 'other' },
];

const PROJECT_OPTIONS: Array<{ value: ProjectFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'adulting', label: 'Adulting' },
  { value: 'kindred', label: 'Kindred' },
  { value: 'adhd', label: 'ADHD' },
  { value: 'other', label: 'Other' },
];

const SOURCE_OPTIONS: Array<{ value: SourceType; label: string }> = [
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'employment', label: 'Employment' },
  { value: 'other', label: 'Other' },
];

const RECURRING_OPTIONS: Array<{ value: RecurringPeriod; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const CATEGORY_LABELS: Record<string, string> = {
  'self-employed-basics': 'Self-employed basics',
  'career-tools': 'Career tools',
  'helpful-links': 'Helpful links',
};

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function formatCurrency(value: number) {
  return `£${value.toFixed(2)}`;
}

function projectLabel(project: ProjectKey | null | undefined) {
  if (project === 'adulting') return 'Adulting';
  if (project === 'kindred') return 'Kindred';
  if (project === 'adhd') return 'ADHD';
  if (project === 'other') return 'Other';
  return 'No project';
}

function sourceTypeLabel(sourceType: SourceType | null | undefined) {
  if (sourceType === 'employment') return 'Employment';
  if (sourceType === 'other') return 'Other';
  return 'Self-employed';
}

function recurringLabel(period: RecurringPeriod | null | undefined) {
  if (period === 'monthly') return 'Monthly';
  if (period === 'yearly') return 'Yearly';
  return 'One-off';
}

function getDaysBadge(dateStr: string) {
  const days = differenceInDays(parseISO(dateStr), startOfDay(new Date()));
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, cls: 'bg-red-100 text-red-800' };
  if (days < 30) return { text: `${days}d`, cls: 'bg-red-100 text-red-800' };
  if (days < 60) return { text: `${days}d`, cls: 'bg-amber-100 text-amber-800' };
  return { text: `${days}d`, cls: 'bg-green-100 text-green-800' };
}

function scrollToRef(ref: { current: HTMLDivElement | null }) {
  window.requestAnimationFrame(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function SelectField({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <AppNativeSelect
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </AppNativeSelect>
  );
}

function MetricCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'positive' | 'negative';
}) {
  const valueColor =
    tone === 'positive'
      ? '$success'
      : tone === 'negative'
        ? '$destructive'
        : '$color';

  return (
    <AppCard backgroundColor="$background" p="$sm">
      <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
        {label}
      </AppText>
      <AppText fontSize="$lg" fontWeight="700" mt="$xs" color={valueColor}>
        {value}
      </AppText>
    </AppCard>
  );
}

export default function SelfEmployedTab() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [incomeEntries, setIncomeEntries] = useState<IncomeEntry[]>([]);
  const [businessExpenses, setBusinessExpenses] = useState<ExpenseEntry[]>([]);
  const [workInvoices, setWorkInvoices] = useState<WorkInvoiceEntry[]>([]);
  const [guidanceCards, setGuidanceCards] = useState<ContentCard[]>([]);
  const [businessBankAccounts, setBusinessBankAccounts] = useState<WorkBusinessBankAccountInput[]>([]);
  const [businessBankTransactions, setBusinessBankTransactions] = useState<WorkBusinessBankTransactionInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeProject, setActiveProject] = useState<ProjectFilter>('all');
  const [showAddDeadline, setShowAddDeadline] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [openSections, setOpenSections] = useState({
    snapshot: false,
    guidance: false,
    money: false,
    deadlines: false,
    invoices: false,
  });

  const [dlTitle, setDlTitle] = useState('');
  const [dlDate, setDlDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dlType, setDlType] = useState<HmrcDeadlineType>('other');
  const [dlSaving, setDlSaving] = useState(false);

  const [incAmount, setIncAmount] = useState('');
  const [incDate, setIncDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [incSource, setIncSource] = useState('');
  const [incSourceType, setIncSourceType] = useState<SourceType>('self_employed');
  const [incProject, setIncProject] = useState<ProjectKey>('adulting');
  const [incNote, setIncNote] = useState('');
  const [incSaving, setIncSaving] = useState(false);

  const [expAmount, setExpAmount] = useState('');
  const [expDate, setExpDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expLabel, setExpLabel] = useState('');
  const [expProject, setExpProject] = useState<ProjectKey>('adulting');
  const [expRecurring, setExpRecurring] = useState(false);
  const [expRecurringPeriod, setExpRecurringPeriod] = useState<RecurringPeriod>('monthly');
  const [expNote, setExpNote] = useState('');
  const [expSaving, setExpSaving] = useState(false);

  const guidanceRef = useRef<HTMLDivElement | null>(null);
  const incomeRef = useRef<HTMLDivElement | null>(null);
  const expenseRef = useRef<HTMLDivElement | null>(null);
  const deadlinesRef = useRef<HTMLDivElement | null>(null);
  const invoicesRef = useRef<HTMLDivElement | null>(null);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setDeadlines([]);
      setIncomeEntries([]);
      setBusinessExpenses([]);
      setWorkInvoices([]);
      setGuidanceCards([]);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const [deadlineResult, incomeResult, expenseResult, contentResult, invoiceResult, businessBankAccountsResult] = await Promise.all([
        supabase.from('hmrc_deadlines').select('*').eq('user_id', user.id).order('deadline_date', { ascending: true }),
        supabase.from('income_log').select('*').eq('user_id', user.id).order('received_date', { ascending: false }),
        supabase.from('expenses').select('*').eq('user_id', user.id).eq('expense_scope', 'business').order('date', { ascending: false }),
        supabase
          .from('content_cards')
          .select('id, category, title, summary, body, icon, link_label, link_url, last_reviewed_at, sort_order')
          .eq('section', 'work-self-employed')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('work_invoices' as any)
          .select('id, invoice_number, client_name, due_date, amount, status')
          .eq('user_id', user.id)
          .neq('status', 'paid')
          .order('due_date', { ascending: true }),
        supabase
          .from('bank_accounts' as any)
          .select('id, institution_name, name, currency, balance, last_synced')
          .eq('user_id', user.id)
          .eq('account_role', 'self_employed_business')
          .order('last_synced', { ascending: false }),
      ]);

      if (deadlineResult.error) throw deadlineResult.error;
      if (incomeResult.error) throw incomeResult.error;
      if (expenseResult.error) throw expenseResult.error;
      if (contentResult.error) throw contentResult.error;
      if (invoiceResult.error) throw invoiceResult.error;
      if (businessBankAccountsResult.error) throw businessBankAccountsResult.error;

      const businessAccountRows = ((businessBankAccountsResult.data || []) as any[]).map((entry) => ({
        id: entry.id,
        institutionName: entry.institution_name || null,
        accountName: entry.name || null,
        currency: entry.currency || null,
        balance: entry.balance == null ? null : Number(entry.balance),
        lastSynced: entry.last_synced || null,
      })) satisfies WorkBusinessBankAccountInput[];
      const businessAccountIds = businessAccountRows.map((entry) => entry.id);
      const oneYearAgo = new Date(Date.now() - 366 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const businessBankTransactionsResult = businessAccountIds.length
        ? await supabase
          .from('bank_transactions' as any)
          .select('id, account_id, booking_date, amount, currency, description, creditor_name, debtor_name')
          .eq('user_id', user.id)
          .in('account_id', businessAccountIds)
          .gte('booking_date', oneYearAgo)
          .order('booking_date', { ascending: false })
        : { data: [], error: null };

      if (businessBankTransactionsResult.error) throw businessBankTransactionsResult.error;

      let nextDeadlines = (deadlineResult.data as Deadline[]) || [];

      if (nextDeadlines.length === 0) {
        const { error: seedError } = await supabase.from('hmrc_deadlines').insert(
          DEFAULT_DEADLINES.map((deadline) => ({ ...deadline, user_id: user.id })) as any
        );

        if (seedError) throw seedError;

        const { data: seededDeadlines, error: seededError } = await supabase
          .from('hmrc_deadlines')
          .select('*')
          .eq('user_id', user.id)
          .order('deadline_date', { ascending: true });

        if (seededError) throw seededError;
        nextDeadlines = (seededDeadlines as Deadline[]) || [];
      }

      setDeadlines(nextDeadlines);
      setIncomeEntries(
        (((incomeResult.data || []) as IncomeEntry[]).map((entry) => ({
          ...entry,
          amount: Number(entry.amount || 0),
        })))
      );
      setBusinessExpenses(
        (((expenseResult.data || []) as ExpenseEntry[]).map((entry) => ({
          ...entry,
          amount: Number(entry.amount || 0),
          is_recurring: Boolean(entry.is_recurring),
        })))
      );
      setWorkInvoices(
        (((invoiceResult.data || []) as any[]).map((entry) => ({
          id: entry.id,
          invoiceNumber: entry.invoice_number,
          clientName: entry.client_name,
          dueDate: entry.due_date,
          amount: Number(entry.amount || 0),
          status: entry.status,
        })))
      );
      setGuidanceCards((contentResult.data as ContentCard[]) || []);
      setBusinessBankAccounts(businessAccountRows);
      setBusinessBankTransactions(
        (((businessBankTransactionsResult.data || []) as any[]).map((entry) => ({
          transactionId: entry.id,
          accountId: entry.account_id,
          bookingDate: entry.booking_date,
          amount: Number(entry.amount || 0),
          currency: entry.currency || 'GBP',
          description: entry.description || null,
          creditorName: entry.creditor_name || null,
          debtorName: entry.debtor_name || null,
        })))
      );
    } catch (error) {
      console.error(error);
      setLoadError('Could not load your self-employed basics right now.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const filteredIncomeEntries = useMemo(
    () =>
      incomeEntries.filter((entry) => {
        if (activeProject === 'all') return true;
        return entry.project === activeProject;
      }),
    [activeProject, incomeEntries],
  );

  const filteredBusinessExpenses = useMemo(
    () =>
      businessExpenses.filter((entry) => {
        if (activeProject === 'all') return true;
        return entry.project === activeProject;
      }),
    [activeProject, businessExpenses],
  );

  const totalInvested = useMemo(
    () => filteredBusinessExpenses.reduce((sum, entry) => sum + entry.amount, 0),
    [filteredBusinessExpenses],
  );

  const monthlyRunningCost = useMemo(
    () =>
      filteredBusinessExpenses.reduce((sum, entry) => {
        if (!entry.is_recurring) return sum;
        if (entry.recurring_period === 'yearly') return sum + entry.amount / 12;
        if (entry.recurring_period === 'monthly') return sum + entry.amount;
        return sum;
      }, 0),
    [filteredBusinessExpenses],
  );

  const currentMonthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const currentMonthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const incomeThisMonth = useMemo(
    () =>
      filteredIncomeEntries.reduce((sum, entry) => {
        const sourceType = entry.source_type || 'self_employed';
        const inMonth = entry.received_date >= currentMonthStart && entry.received_date <= currentMonthEnd;
        if (!inMonth || sourceType !== 'self_employed') return sum;
        return sum + entry.amount;
      }, 0),
    [currentMonthEnd, currentMonthStart, filteredIncomeEntries],
  );

  const profitLossThisMonth = incomeThisMonth - monthlyRunningCost;
  const cashflowSnapshot = useMemo(
    () =>
      buildWorkCashflowSnapshot({
        incomeEntries: filteredIncomeEntries.map((entry) => ({
          amount: entry.amount,
          receivedDate: entry.received_date,
        })),
        invoices: workInvoices,
        monthlyRunningCost,
      }),
    [filteredIncomeEntries, monthlyRunningCost, workInvoices],
  );
  const businessBankProjection = useMemo(
    () => buildWorkBusinessBankProjection({
      accounts: businessBankAccounts,
      transactions: businessBankTransactions,
    }),
    [businessBankAccounts, businessBankTransactions],
  );
  const businessBankLatestSyncLabel = businessBankProjection.latestSync
    ? format(parseISO(businessBankProjection.latestSync), 'd MMM yyyy')
    : null;

  const guidanceGroups = useMemo(() => {
    const groups: Array<{ category: string; cards: ContentCard[] }> = [];

    for (const card of guidanceCards) {
      if (card.category === 'helpful-links') continue;

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.category === card.category) {
        lastGroup.cards.push(card);
      } else {
        groups.push({ category: card.category, cards: [card] });
      }
    }

    return groups;
  }, [guidanceCards]);

  const helpfulLinks = useMemo(
    () => guidanceCards.filter((card) => card.category === 'helpful-links'),
    [guidanceCards],
  );

  const toggleDeadline = async (deadline: Deadline) => {
    const { error } = await supabase
      .from('hmrc_deadlines')
      .update({ completed: !deadline.completed } as any)
      .eq('id', deadline.id);

    if (error) {
      console.error(error);
      toast.error('Could not update that deadline.');
      return;
    }

    notifySectionRefresh();
    void fetchAll();
  };

  const saveDeadline = async () => {
    if (!user || !dlTitle.trim() || !dlDate) return;

    setDlSaving(true);
    const { error } = await supabase.from('hmrc_deadlines').insert({
      user_id: user.id,
      title: dlTitle.trim(),
      deadline_date: dlDate,
      type: dlType,
    } as any);

    if (error) {
      console.error(error);
      toast.error('Could not save that deadline.');
      setDlSaving(false);
      return;
    }

    setDlSaving(false);
    setDlTitle('');
    setDlDate(format(new Date(), 'yyyy-MM-dd'));
    setDlType('other');
    setShowAddDeadline(false);
    notifySectionRefresh();
    void fetchAll();
  };

  const openDeadlineTemplate = (type: HmrcDeadlineType) => {
    setShowAddDeadline(true);
    setDlType(type);
    setDlTitle((current) => current.trim() || HMRC_DEADLINE_SUPPORT[type].defaultTitle);
    scrollToRef(deadlinesRef);
  };

  const saveIncome = async () => {
    if (!user || !incAmount || !incDate) return;

    setIncSaving(true);
    const { error } = await supabase.from('income_log').insert({
      user_id: user.id,
      amount: Number(incAmount),
      source: incSource.trim() || null,
      source_type: incSourceType,
      project: incProject,
      notes: incNote.trim() || null,
      received_date: incDate,
    } as any);

    if (error) {
      console.error(error);
      toast.error('Could not save that income entry.');
      setIncSaving(false);
      return;
    }

    setIncSaving(false);
    setIncAmount('');
    setIncDate(format(new Date(), 'yyyy-MM-dd'));
    setIncSource('');
    setIncSourceType('self_employed');
    setIncProject('adulting');
    setIncNote('');
    setShowAddIncome(false);
    notifySectionRefresh();
    void fetchAll();
  };

  const saveExpense = async () => {
    if (!user || !expAmount || !expDate || !expLabel.trim()) return;

    setExpSaving(true);
    const { error } = await supabase.from('expenses').insert({
      user_id: user.id,
      amount: Number(expAmount),
      category: expLabel.trim(),
      description: expLabel.trim(),
      date: expDate,
      expense_scope: 'business',
      project: expProject,
      is_recurring: expRecurring,
      recurring_period: expRecurring ? expRecurringPeriod : 'none',
      notes: expNote.trim() || null,
    } as any);

    if (error) {
      console.error(error);
      toast.error('Could not save that business expense.');
      setExpSaving(false);
      return;
    }

    setExpSaving(false);
    setExpAmount('');
    setExpDate(format(new Date(), 'yyyy-MM-dd'));
    setExpLabel('');
    setExpProject('adulting');
    setExpRecurring(false);
    setExpRecurringPeriod('monthly');
    setExpNote('');
    setShowAddExpense(false);
    notifySectionRefresh();
    void fetchAll();
  };

  const openIncomeForm = () => {
    setShowAddIncome(true);
    scrollToRef(incomeRef);
  };

  const openExpenseForm = () => {
    setShowAddExpense(true);
    scrollToRef(expenseRef);
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  if (loading) {
    return <AppLoadingState title="Loading self-employed basics" body="Pulling guidance, tracking, and the latest work admin." />;
  }

  if (loadError) {
    return (
      <AppErrorState
        title="Self-employed basics are unavailable"
        body={loadError}
        action={
          <AppButton tone="outline" onPress={() => void fetchAll()}>
            Try again
          </AppButton>
        }
      />
    );
  }

  const pendingDeadlines = deadlines
    .filter((deadline) => !deadline.completed)
    .sort((a, b) => a.deadline_date.localeCompare(b.deadline_date));
  const completedDeadlines = deadlines.filter((deadline) => deadline.completed);

  return (
    <div className="space-y-5">
      <SectionAccordionCard
        emoji="📊"
        title="Business snapshot"
        subtitle="See the live cash picture first so quieter months and quick actions are easier to scan."
        open={openSections.snapshot}
        onToggle={() => toggleSection('snapshot')}
      >
        <div className="space-y-5">
          <AppCard>
            <AppCardHeader>
              <div className="space-y-3">
                <div>
                  <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                    Business snapshot
                  </AppText>
                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                    A simple view of what your self-employed work is costing and bringing in.
                  </AppText>
                </div>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_OPTIONS.map((option) => {
                    const active = activeProject === option.value;
                    return (
                      <AppChipButton
                        key={option.value}
                        type="button"
                        onClick={() => setActiveProject(option.value)}
                        active={active}
                        className={active ? 'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground' : undefined}
                      >
                        {option.label}
                      </AppChipButton>
                    );
                  })}
                </div>
              </div>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <MetricCard label="Total invested" value={formatCurrency(totalInvested)} />
                <MetricCard label="Monthly running cost" value={formatCurrency(monthlyRunningCost)} />
                <MetricCard label="Income this month" value={formatCurrency(incomeThisMonth)} tone="positive" />
                <MetricCard
                  label="Profit / loss this month"
                  value={formatCurrency(profitLossThisMonth)}
                  tone={profitLossThisMonth < 0 ? 'negative' : 'positive'}
                />
              </div>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Income smoothing
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Keep the lower month visible, not just the better one, so work cashflow stays realistic.
                </AppText>
              </div>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <MetricCard
                  label="Typical month"
                  value={cashflowSnapshot.typicalMonthlyIncome === null ? 'Build history' : formatCurrency(cashflowSnapshot.typicalMonthlyIncome)}
                />
                <MetricCard
                  label="Low-month floor"
                  value={cashflowSnapshot.lowMonthFloor === null ? '—' : formatCurrency(cashflowSnapshot.lowMonthFloor)}
                  tone={cashflowSnapshot.lowMonthAlert?.level === 'high' ? 'negative' : 'default'}
                />
                <MetricCard
                  label="Last full month"
                  value={cashflowSnapshot.lastCompleteMonth ? formatCurrency(cashflowSnapshot.lastCompleteMonth.totalIncome) : '—'}
                  tone={cashflowSnapshot.lowMonthDetected ? 'negative' : 'default'}
                />
                <MetricCard
                  label="Month-end expected"
                  value={cashflowSnapshot.currentMonthExpectedIncome === null ? '—' : formatCurrency(cashflowSnapshot.currentMonthExpectedIncome)}
                  tone={cashflowSnapshot.currentMonthWatch ? 'negative' : 'default'}
                />
              </div>

              {cashflowSnapshot.lowMonthAlert ? (
                <AppInlineAlert title={cashflowSnapshot.lowMonthAlert.title} tone={cashflowSnapshot.lowMonthAlert.level === 'high' ? 'error' : 'warning'}>
                  {cashflowSnapshot.lowMonthAlert.summary}
                </AppInlineAlert>
              ) : cashflowSnapshot.currentMonthAlert ? (
                <AppInlineAlert title={cashflowSnapshot.currentMonthAlert.title} tone={cashflowSnapshot.currentMonthAlert.level === 'high' ? 'error' : 'warning'}>
                  {cashflowSnapshot.currentMonthAlert.summary}
                </AppInlineAlert>
              ) : (
                <AppInlineAlert title="No low-month warning right now">
                  Adulting is now comparing recent income history against a safer floor, so quieter months stop sneaking up on essentials.
                </AppInlineAlert>
              )}

              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {cashflowSnapshot.months.map((month) => (
                  <AppCard key={month.monthKey} backgroundColor="$background" p="$sm">
                    <AppText fontSize="$sm" fontWeight="600" color="$colorMuted">
                      {month.label}
                    </AppText>
                    <AppText fontSize="$sm" fontWeight="700" mt="$xs">
                      {formatCurrency(month.totalIncome)}
                    </AppText>
                  </AppCard>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppButton tone="outline" onPress={() => navigate('/money?tab=survival')}>
                  Open Survival
                </AppButton>
                <AppButton tone="outline" onPress={() => scrollToRef(invoicesRef)}>
                  Review invoices
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>

          {businessBankProjection.accountCount > 0 ? (
            <AppCard>
              <AppCardHeader>
                <div>
                  <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                    Connected business bank feed
                  </AppText>
                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                    Accounts marked as self-employed business in My Settings now feed Work without touching your personal Money view.
                  </AppText>
                </div>
              </AppCardHeader>
              <AppCardBody>
                <AppInlineAlert title="Business accounts stay separate">
                  {businessBankProjection.analysis
                    ? `Imported from ${businessBankProjection.accountLabels.join(', ')}${businessBankLatestSyncLabel ? ` · latest sync ${businessBankLatestSyncLabel}` : ''}.`
                    : `Adulting can see ${businessBankProjection.accountLabels.join(', ')}, but imported business transactions are not ready yet.`}
                </AppInlineAlert>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <MetricCard label="Business accounts" value={String(businessBankProjection.accountCount)} />
                  <MetricCard label="Imported income this month" value={formatCurrency(businessBankProjection.currentMonthIncome)} tone="positive" />
                  <MetricCard label="Imported spend this month" value={formatCurrency(businessBankProjection.currentMonthSpend)} />
                  <MetricCard label="Needs review" value={String(businessBankProjection.reviewQueueCount)} tone={businessBankProjection.reviewQueueCount > 0 ? 'negative' : 'default'} />
                </div>
                {businessBankProjection.cashflowSnapshot ? (
                  <>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <MetricCard
                        label="Bank-typical month"
                        value={
                          businessBankProjection.cashflowSnapshot.typicalMonthlyIncome === null
                            ? 'Build history'
                            : formatCurrency(businessBankProjection.cashflowSnapshot.typicalMonthlyIncome)
                        }
                      />
                      <MetricCard
                        label="Bank low-month floor"
                        value={
                          businessBankProjection.cashflowSnapshot.lowMonthFloor === null
                            ? '—'
                            : formatCurrency(businessBankProjection.cashflowSnapshot.lowMonthFloor)
                        }
                        tone={businessBankProjection.cashflowSnapshot.lowMonthAlert?.level === 'high' ? 'negative' : 'default'}
                      />
                    </div>
                    {businessBankProjection.cashflowSnapshot.lowMonthAlert ? (
                      <AppInlineAlert title={businessBankProjection.cashflowSnapshot.lowMonthAlert.title} tone={businessBankProjection.cashflowSnapshot.lowMonthAlert.level === 'high' ? 'error' : 'warning'}>
                        {businessBankProjection.cashflowSnapshot.lowMonthAlert.summary}
                      </AppInlineAlert>
                    ) : null}
                  </>
                ) : null}
                {businessBankProjection.analysis?.recurringIncomes.length ? (
                  <div className="space-y-2">
                    <AppText fontSize="$sm" fontWeight="700">Likely recurring business income</AppText>
                    {businessBankProjection.analysis.recurringIncomes.slice(0, 3).map((entry) => (
                      <AppCard key={`${entry.source}-${entry.latestBookingDate}`} backgroundColor="$background" p="$sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <AppText fontSize="$sm" fontWeight="700">{entry.source}</AppText>
                            <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                              {entry.cadence} · latest {format(parseISO(entry.latestBookingDate), 'd MMM yyyy')}
                            </AppText>
                          </div>
                          <AppText fontSize="$sm" fontWeight="700">{formatCurrency(entry.monthlyEquivalent)} / month</AppText>
                        </div>
                      </AppCard>
                    ))}
                  </div>
                ) : null}
                {businessBankProjection.analysis?.recurringOutgoings.length ? (
                  <div className="space-y-2">
                    <AppText fontSize="$sm" fontWeight="700">Likely recurring business costs</AppText>
                    {businessBankProjection.analysis.recurringOutgoings.slice(0, 3).map((entry) => (
                      <AppCard key={`${entry.merchant}-${entry.latestBookingDate}`} backgroundColor="$background" p="$sm">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <AppText fontSize="$sm" fontWeight="700">{entry.merchant}</AppText>
                            <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                              {entry.cadence} · {entry.category}
                            </AppText>
                          </div>
                          <AppText fontSize="$sm" fontWeight="700">{formatCurrency(entry.monthlyEquivalent)} / month</AppText>
                        </div>
                      </AppCard>
                    ))}
                  </div>
                ) : null}
              </AppCardBody>
            </AppCard>
          ) : null}

          <AppCard>
            <AppCardHeader>
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Quick actions
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Track the basics so nothing bites you later.
                </AppText>
              </div>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppButton tone="outline" onPress={openIncomeForm}>
                  Log income
                </AppButton>
                <AppButton tone="outline" onPress={openExpenseForm}>
                  Log expense
                </AppButton>
                <AppButton tone="outline" onPress={() => scrollToRef(deadlinesRef)}>
                  View deadlines
                </AppButton>
                <AppButton tone="outline" onPress={() => scrollToRef(guidanceRef)}>
                  Open official guidance
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧠"
        title="Guidance and playbooks"
        subtitle="Keep the official self-employed basics and broader work playbooks together."
        open={openSections.guidance}
        onToggle={() => toggleSection('guidance')}
      >
        <div className="space-y-5">
          <div ref={guidanceRef}>
            <AppCard>
              <AppCardHeader>
                <div>
                  <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                    Self-employed basics
                  </AppText>
                  <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                    Plain-English official guidance in the same place you do the admin.
                  </AppText>
                </div>
              </AppCardHeader>
              <AppCardBody>
                {guidanceGroups.length === 0 ? (
                  <AppEmptyState
                    title="No guidance cards yet"
                    body="Official basics will show here once the content cards are live in Supabase."
                  />
                ) : (
                  <div className="space-y-4">
                    {guidanceGroups.map((group) => (
                      <div key={group.category} className="space-y-2">
                        <AppText fontSize="$sm" fontWeight="700">
                          {CATEGORY_LABELS[group.category] || group.category}
                        </AppText>
                        {group.cards.map((card) => (
                          <AppCard key={card.id} backgroundColor="$background" p="$sm">
                            <div className="flex items-start gap-3">
                              <span className="text-lg">{card.icon}</span>
                              <div className="min-w-0 flex-1">
                                <AppText fontSize="$sm" fontWeight="700">
                                  {card.title}
                                </AppText>
                                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                                  {card.summary}
                                </AppText>
                                <AppText fontSize="$sm" mt="$sm">
                                  {card.body}
                                </AppText>
                                {card.link_url && (
                                  <a
                                    href={card.link_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary"
                                  >
                                    <ExternalLink size={14} />
                                    {card.link_label || 'Open link'}
                                  </a>
                                )}
                                <AppText fontSize="$sm" color="$colorMuted" mt="$sm">
                                  Last reviewed {format(parseISO(card.last_reviewed_at), 'd MMM yyyy')}
                                </AppText>
                              </div>
                            </div>
                          </AppCard>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </AppCardBody>
            </AppCard>
          </div>

          <AppCard>
            <AppCardHeader>
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Helpful links and playbooks
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Official links stay close by, and the broader work playbooks are still one tap away.
                </AppText>
              </div>
            </AppCardHeader>
            <AppCardBody>
              <AppPanelButton type="button" onClick={() => navigate('/guides?section=work')} className="bg-background">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <AppText fontSize="$sm" fontWeight="700">
                      Open work playbooks
                    </AppText>
                    <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                      Scripts, steps, and calmer guidance for work situations that need more than a quick note.
                    </AppText>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
                </div>
              </AppPanelButton>

              {helpfulLinks.length > 0 ? (
                <div className="space-y-2">
                  {helpfulLinks.map((card) => (
                    <a
                      key={card.id}
                      href={card.link_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 transition-all active:scale-[0.98]"
                    >
                      <span className="text-lg">{card.icon}</span>
                      <div className="min-w-0 flex-1">
                        <AppText fontSize="$sm" fontWeight="700">
                          {card.title}
                        </AppText>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          {card.summary}
                        </AppText>
                      </div>
                      <ExternalLink size={14} className="shrink-0 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              ) : null}
            </AppCardBody>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="💷"
        title="Income and expenses"
        subtitle="Keep money in and money out together instead of splitting the scan across the page."
        open={openSections.money}
        onToggle={() => toggleSection('money')}
      >
        <div className="space-y-5">
          {businessBankProjection.accountCount > 0 ? (
            <AppInlineAlert title="Manual forms only when the bank feed cannot cover it">
              Connected business-bank history now shows in the Business snapshot above. Use the forms below for cash, one-off items, or anything that has not landed in the bank yet.
            </AppInlineAlert>
          ) : null}
          <div ref={incomeRef}>
        <AppCard>
          <AppCardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Income log
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Keep business income visible without turning this into accounting software.
                </AppText>
              </div>
              <AppButton size="sm" onPress={() => setShowAddIncome((current) => !current)}>
                <Plus size={12} />
                {showAddIncome ? 'Cancel' : 'Log income'}
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardBody>
            {showAddIncome && (
              <AppCard backgroundColor="$mutedBackground" p="$sm">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Amount">
                    <AppInput
                      tag="input"
                      type="number"
                      inputMode="decimal"
                      value={incAmount}
                      onChange={(event) => setIncAmount((event.target as HTMLInputElement).value)}
                      placeholder="0.00"
                    />
                  </AppFieldGroup>
                  <AppDateField label="Date" value={incDate} onChange={setIncDate} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Income type">
                    <SelectField
                      value={incSourceType}
                      onChange={(value) => setIncSourceType(value as SourceType)}
                      options={SOURCE_OPTIONS}
                    />
                  </AppFieldGroup>
                  <AppFieldGroup label="Project">
                    <SelectField
                      value={incProject}
                      onChange={(value) => setIncProject(value as ProjectKey)}
                      options={PROJECT_OPTIONS.filter((option) => option.value !== 'all') as Array<{ value: string; label: string }>}
                    />
                  </AppFieldGroup>
                </div>
                <AppFieldGroup label="Label (optional)" helper="Useful for a client, project, or what the payment was for.">
                  <AppInput
                    tag="input"
                    value={incSource}
                    onChange={(event) => setIncSource((event.target as HTMLInputElement).value)}
                    placeholder="What this was for"
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Note (optional)">
                  <AppTextArea
                    rows={3}
                    value={incNote}
                    onChange={(event) => setIncNote((event.target as HTMLTextAreaElement).value)}
                    placeholder="Anything worth remembering later"
                  />
                </AppFieldGroup>
                <AppButton width="100%" onPress={saveIncome} disabled={!incAmount || !incDate || incSaving}>
                  {incSaving ? 'Saving...' : 'Save income'}
                </AppButton>
              </AppCard>
            )}

            {filteredIncomeEntries.length === 0 ? (
              <AppEmptyState
                title="No income logged yet"
                body="Add the money coming in so your work stops living only in your head."
              />
            ) : (
              <div className="space-y-3">
                {filteredIncomeEntries.map((entry) => (
                  <AppCard key={entry.id} backgroundColor="$background" p="$sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <AppText fontSize="$sm" fontWeight="700">
                            {projectLabel(entry.project)}
                          </AppText>
                          {entry.source_type && entry.source_type !== 'self_employed' ? (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                              {sourceTypeLabel(entry.source_type)}
                            </span>
                          ) : null}
                        </div>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          {format(parseISO(entry.received_date), 'd MMM yyyy')}
                        </AppText>
                        {entry.source ? (
                          <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                            {entry.source}
                          </AppText>
                        ) : null}
                        {entry.notes ? (
                          <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                            {entry.notes}
                          </AppText>
                        ) : null}
                      </div>
                      <AppText fontSize="$sm" fontWeight="700">
                        {formatCurrency(entry.amount)}
                      </AppText>
                    </div>
                  </AppCard>
                ))}
              </div>
            )}
          </AppCardBody>
        </AppCard>
      </div>

      <div ref={expenseRef}>
        <AppCard>
          <AppCardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  Expense log
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Keep business costs separate from personal spending.
                </AppText>
              </div>
              <AppButton size="sm" onPress={() => setShowAddExpense((current) => !current)}>
                <Plus size={12} />
                {showAddExpense ? 'Cancel' : 'Log expense'}
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardBody>
            <AppInlineAlert title="Simple by design">
              This tracks the basics: business costs, recurring running costs, and project tags.
            </AppInlineAlert>

            {showAddExpense && (
              <AppCard backgroundColor="$mutedBackground" p="$sm">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Amount">
                    <AppInput
                      tag="input"
                      type="number"
                      inputMode="decimal"
                      value={expAmount}
                      onChange={(event) => setExpAmount((event.target as HTMLInputElement).value)}
                      placeholder="0.00"
                    />
                  </AppFieldGroup>
                  <AppDateField label="Date" value={expDate} onChange={setExpDate} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Label">
                    <AppInput
                      tag="input"
                      value={expLabel}
                      onChange={(event) => setExpLabel((event.target as HTMLInputElement).value)}
                      placeholder="Hosting, software, phone bill"
                    />
                  </AppFieldGroup>
                  <AppFieldGroup label="Project">
                    <SelectField
                      value={expProject}
                      onChange={(value) => setExpProject(value as ProjectKey)}
                      options={PROJECT_OPTIONS.filter((option) => option.value !== 'all') as Array<{ value: string; label: string }>}
                    />
                  </AppFieldGroup>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Repeats?">
                    <SelectField
                      value={expRecurring ? 'yes' : 'no'}
                      onChange={(value) => setExpRecurring(value === 'yes')}
                      options={[
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                      ]}
                    />
                  </AppFieldGroup>
                  {expRecurring ? (
                    <AppFieldGroup label="Frequency">
                      <SelectField
                        value={expRecurringPeriod}
                        onChange={(value) => setExpRecurringPeriod(value as RecurringPeriod)}
                        options={RECURRING_OPTIONS}
                      />
                    </AppFieldGroup>
                  ) : null}
                </div>
                <AppFieldGroup label="Note (optional)">
                  <AppTextArea
                    rows={3}
                    value={expNote}
                    onChange={(event) => setExpNote((event.target as HTMLTextAreaElement).value)}
                    placeholder="Anything worth keeping with the entry"
                  />
                </AppFieldGroup>
                <AppButton width="100%" onPress={saveExpense} disabled={!expAmount || !expDate || !expLabel.trim() || expSaving}>
                  {expSaving ? 'Saving...' : 'Save expense'}
                </AppButton>
              </AppCard>
            )}

            {filteredBusinessExpenses.length === 0 ? (
              <AppEmptyState
                title="No business expenses yet"
                body="Add the costs you are carrying so your work stays grounded in real numbers."
              />
            ) : (
              <div className="space-y-3">
                {filteredBusinessExpenses.map((entry) => (
                  <AppCard key={entry.id} backgroundColor="$background" p="$sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <AppText fontSize="$sm" fontWeight="700">
                            {entry.category || entry.description || 'Business expense'}
                          </AppText>
                          {entry.is_recurring ? (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                              {recurringLabel(entry.recurring_period)}
                            </span>
                          ) : null}
                        </div>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          {format(parseISO(entry.date), 'd MMM yyyy')} · {projectLabel(entry.project)}
                        </AppText>
                        {entry.notes ? (
                          <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                            {entry.notes}
                          </AppText>
                        ) : null}
                      </div>
                      <AppText fontSize="$sm" fontWeight="700">
                        {formatCurrency(entry.amount)}
                      </AppText>
                    </div>
                  </AppCard>
                ))}
              </div>
            )}
          </AppCardBody>
        </AppCard>
      </div>
      </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="📅"
        title="HMRC deadlines"
        subtitle="Keep the next tax dates visible without them taking over the whole screen."
        open={openSections.deadlines}
        onToggle={() => toggleSection('deadlines')}
      >
        <div ref={deadlinesRef}>
          <AppCard>
          <AppCardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                  HMRC deadlines
                </AppText>
                <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                  Keep deadlines visible without letting them take over the whole screen. Adulting flags the 1 month, 2 week, and 3 day windows here.
                </AppText>
              </div>
              <AppButton size="sm" onPress={() => setShowAddDeadline((current) => !current)}>
                <Plus size={12} />
                {showAddDeadline ? 'Cancel' : 'Add'}
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardBody>
            <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {HMRC_DEADLINE_TYPE_OPTIONS.filter((option) => option.value !== 'other').map((option) => (
                <AppButton
                  key={option.value}
                  size="sm"
                  tone="outline"
                  onPress={() => openDeadlineTemplate(option.value)}
                >
                  {option.label}
                </AppButton>
              ))}
            </div>
            <div className="mb-3">
              <AppInlineAlert title="Reminder rhythm">
                Adulting now keeps the same HMRC cadence visible on each deadline: 1 month, 2 weeks, 3 days, then a missed-state prompt if something slips. VAT returns use the same rhythm without being seeded for everyone.
              </AppInlineAlert>
            </div>
            {showAddDeadline && (
              <AppCard backgroundColor="$mutedBackground" p="$sm">
                <AppFieldGroup label="Deadline type">
                  <SelectField
                    value={dlType}
                    onChange={(value) => setDlType(value as HmrcDeadlineType)}
                    options={HMRC_DEADLINE_TYPE_OPTIONS as Array<{ value: string; label: string }>}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Deadline title">
                  <AppInput
                    tag="input"
                    value={dlTitle}
                    onChange={(event) => setDlTitle((event.target as HTMLInputElement).value)}
                    placeholder={HMRC_DEADLINE_SUPPORT[dlType].defaultTitle}
                  />
                </AppFieldGroup>
                <AppDateField label="Date" value={dlDate} onChange={setDlDate} />
                <AppButton width="100%" onPress={saveDeadline} disabled={!dlTitle.trim() || !dlDate || dlSaving}>
                  {dlSaving ? 'Saving...' : 'Save deadline'}
                </AppButton>
              </AppCard>
            )}

            {pendingDeadlines.length === 0 && completedDeadlines.length === 0 ? (
              <AppEmptyState
                title="No deadlines yet"
                body="Adulting will seed the standard self-assessment dates once your work tools are active."
              />
            ) : (
              <div className="space-y-2">
                {pendingDeadlines.map((deadline) => {
                  const badge = getDaysBadge(deadline.deadline_date);
                  const reminderWindow = getHmrcReminderWindowLabel(deadline.deadline_date);
                  const support = HMRC_DEADLINE_SUPPORT[(deadline.type as HmrcDeadlineType)] || HMRC_DEADLINE_SUPPORT.other;
                  const actionNow = getHmrcDeadlineAction(deadline.type, deadline.deadline_date);
                  return (
                    <div key={deadline.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-3">
                      <AppIconButton
                        type="button"
                        variant="outline"
                        onClick={() => void toggleDeadline(deadline)}
                        className="h-6 w-6 shrink-0 rounded-md border-2 border-border"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <AppText fontSize="$sm" fontWeight="700" className="truncate">
                            {deadline.title}
                          </AppText>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {support.label}
                          </span>
                        </div>
                        <AppText fontSize="$sm" color="$colorMuted">
                          {format(parseISO(deadline.deadline_date), 'd MMM yyyy')}
                        </AppText>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          {reminderWindow}
                        </AppText>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          Action now: {actionNow}
                        </AppText>
                        <AppText fontSize="$sm" color="$colorMuted" mt="$xs">
                          {support.summary}
                        </AppText>
                        <a
                          href={support.linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary"
                        >
                          <ExternalLink size={14} />
                          {support.linkLabel}
                        </a>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                        {badge.text}
                      </span>
                    </div>
                  );
                })}

                {completedDeadlines.length > 0 ? (
                  <div className="space-y-2 border-t border-border pt-2">
                    {completedDeadlines.map((deadline) => (
                      <div key={deadline.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 opacity-60">
                        <AppIconButton
                          type="button"
                          variant="outline"
                          onClick={() => void toggleDeadline(deadline)}
                          className="h-6 w-6 shrink-0 rounded-md border-2 border-primary bg-primary/10"
                        >
                          <Check size={14} className="text-primary" />
                        </AppIconButton>
                        <AppText fontSize="$sm" color="$colorMuted" className="flex-1 truncate line-through">
                          {deadline.title}
                        </AppText>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </AppCardBody>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧾"
        title="Invoices"
        subtitle="Track what is owed to you and keep chasing in the same work flow."
        open={openSections.invoices}
        onToggle={() => toggleSection('invoices')}
      >
        <div ref={invoicesRef}>
          <InvoiceTracker onDataChanged={() => void fetchAll()} />
        </div>
      </SectionAccordionCard>
    </div>
  );
}
